import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { scrollToId } from "@/lib/site";
import { trackEvent, trackLead } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOSHA = ["vata", "pitta", "kapha"]; // hidden: option index 0/1/2 => vata/pitta/kapha, 3 => no score

const QUESTIONS = [
  {
    q: "When your child needs to communicate something, what do you commonly notice?",
    domain: "Communication & Initiation",
    options: [
      "Starts quickly, but communication may be inconsistent or scattered.",
      "Communicates strongly and may become frustrated when not understood.",
      "Takes time to initiate communication and may need prompting.",
    ],
  },
  {
    q: "How does your child generally respond to a new environment?",
    domain: "Adaptability & Transitions",
    options: [
      "Becomes restless, uncertain or easily overstimulated.",
      "Observes closely and may become resistant when things are not as expected.",
      "Takes time to engage and prefers familiar surroundings.",
    ],
  },
  {
    q: "What best describes your child's attention?",
    domain: "Attention & Executive Function",
    options: [
      "Attention shifts quickly between activities.",
      "Focuses intensely on preferred activities and may resist interruption.",
      "Can remain with an activity but may take time to get started.",
    ],
  },
  {
    q: "When asked to begin a task, what usually happens?",
    domain: "Task Initiation & Executive Function",
    options: [
      "Starts quickly but may move to something else before completing it.",
      "May resist or become frustrated when the task is not preferred.",
      "Understands the task but often needs repeated prompting to begin.",
    ],
  },
  {
    q: "How would you describe your child's usual activity level?",
    domain: "Activity & Motor Regulation",
    options: [
      "Frequently moving, restless or constantly changing activities.",
      "Active, purposeful and intense when engaged.",
      "Generally calm, slower-paced or less spontaneously active.",
    ],
  },
  {
    q: "What usually happens when a familiar routine suddenly changes?",
    domain: "Flexibility & Transitions",
    options: [
      "Becomes restless, anxious or unsettled.",
      "Becomes irritated, frustrated or resistant.",
      "Withdraws or takes considerable time to adjust.",
    ],
  },
  {
    q: "What usually happens when your child becomes emotionally overwhelmed?",
    domain: "Emotional Regulation",
    options: [
      "Becomes restless, fearful, tearful or difficult to settle.",
      "Shows anger, shouting, strong frustration or intense reactions.",
      "Withdraws, shuts down or becomes less responsive.",
    ],
  },
  {
    q: "How would you describe your child's sleep pattern?",
    domain: "Sleep & Arousal",
    options: [
      "Light or irregular sleep, with difficulty staying asleep.",
      "Difficulty settling when overstimulated, frustrated or emotionally activated.",
      "Sleeps deeply or for longer periods and may be slow to become active after waking.",
    ],
  },
  {
    q: "How does your child generally respond to sounds, touch, textures or busy environments?",
    domain: "Sensory Regulation",
    options: [
      "Becomes easily overwhelmed or appears highly sensitive to sensory input.",
      "Strongly dislikes particular sensations and reacts intensely to them.",
      "May seek stronger sensory input or take longer to respond to sensory information.",
    ],
  },
  {
    q: "If your child shows repetitive movements, which pattern is most common?",
    domain: "Movement & Self-Regulation",
    options: [
      "Movements are frequent or variable and may increase with excitement or restlessness.",
      "Movements become more noticeable during frustration or emotional intensity.",
      "Movements tend to be rhythmic, repetitive and prolonged.",
      "My child does not usually show repetitive movements.",
    ],
  },
  {
    q: "How would you describe your child's appetite?",
    domain: "Appetite / Gut–Brain Function",
    options: [
      "Variable—very hungry at some times and less interested in food at others.",
      "Strong appetite and may become irritable when meals are delayed.",
      "Generally steady appetite and may prefer familiar or heavier foods.",
    ],
  },
  {
    q: "What best describes your child's digestion or bowel pattern?",
    domain: "Digestion / Gut–Brain Function",
    options: [
      "Variable digestion, gas, bloating or tendency toward constipation.",
      "Sensitive digestion, tendency toward loose stools, acidity or heat-related discomfort.",
      "Slower digestion, heaviness or sluggish bowel pattern.",
    ],
  },
  {
    q: "When learning something new, which pattern do you notice most often?",
    domain: "Learning & Memory",
    options: [
      "Learns quickly but may forget or have difficulty maintaining consistency.",
      "Learns with strong concentration when interested or motivated.",
      "Learns more gradually but tends to retain familiar learning once established.",
    ],
  },
  {
    q: "During play, which pattern best describes your child?",
    domain: "Play, Flexibility & Engagement",
    options: [
      "Moves quickly between toys or activities.",
      "Becomes highly focused and may prefer play to happen in a particular way.",
      "Prefers familiar activities and may repeat them for longer periods.",
    ],
  },
  {
    q: "When your child has to wait for something they want, what usually happens?",
    domain: "Impulse Control & Emotional Regulation",
    options: [
      "Becomes restless or impulsive.",
      "Becomes impatient, irritated or frustrated.",
      "Can wait but may need encouragement to shift into the next activity.",
    ],
  },
  {
    q: "How does your child usually engage socially?",
    domain: "Social Engagement",
    options: [
      "Shows interest but engagement may be inconsistent or easily distracted.",
      "Engages strongly but may prefer interaction to happen on their terms.",
      "Takes longer to warm up, engage or initiate interaction.",
    ],
  },
  {
    q: "How would you describe your child's spontaneous communication initiation?",
    domain: "Communication & Initiation",
    options: [
      "Initiates communication, but attempts may be inconsistent or rapidly changing.",
      "Initiates strongly around preferred needs, interests or topics.",
      "Initiation is limited and often requires prompting.",
    ],
  },
  {
    q: "When your child becomes overstimulated, what tends to happen first?",
    domain: "Sensory & Emotional Regulation",
    options: [
      "Restlessness, increased movement or scattered behavior.",
      "Irritability, frustration or intense emotional reaction.",
      "Withdrawal, reduced engagement or slowing down.",
    ],
  },
  {
    q: "Looking at your child's overall day-to-day pattern, which description feels most familiar?",
    domain: "Parent Global Pattern Check",
    scored: false,
    options: [
      "Variable, quick-moving, restless and sensitive.",
      "Intense, determined, reactive and strongly focused.",
      "Steady, slower-paced, calm and sometimes resistant to change.",
    ],
  },
  {
    q: "Across a typical week, which area creates the greatest difficulty for your child?",
    domain: "Primary Functional Concern",
    scored: false,
    options: [
      "Regulation, consistency and settling.",
      "Frustration, rigidity and emotional intensity.",
      "Initiation, engagement and activation.",
    ],
  },
];

const CONCERNS = [
  "Speech", "Attention", "Sensory", "Behavior & Emotional Regulation", "Sleep",
  "Digestion", "Movement", "Learning", "Multiple Concerns", "Other",
];

const DOSHA_LABEL = { vata: "Vata", pitta: "Pitta", kapha: "Kapha" };

const EXPLAIN = {
  single: (d) =>
    `Responses suggest a ${d === "vata" ? "quick-moving, sensitive and variable" : d === "pitta" ? "intense, determined and strongly reactive" : "steady, slower-to-start yet persistent"} pattern. A detailed consultation explores how this appears across your child's functional systems.`,
  dual: (a, b) =>
    `Responses suggest a combination of ${DOSHA_LABEL[a]} and ${DOSHA_LABEL[b]} tendencies. A detailed consultation explores how these patterns appear together across your child's functional systems.`,
  mixed: () =>
    "Responses are distributed across all three patterns without one strongly predominating. A detailed consultation explores your child's broader functional pattern.",
};

const inputCls =
  "w-full bg-white border border-[#E2DACD] rounded-xl px-4 py-3 text-sm text-[#2D3A32] placeholder:text-[#9aa79e] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059] transition";

export default function AssessmentModal({ open, onClose, linkedLead = null }) {
  const [step, setStep] = useState("intro");
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState(Array(20).fill(null));
  const [lead, setLead] = useState({ parent_name: "", child_age: "", whatsapp: "", email: "", city_state: "", concern: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const analysis = useMemo(() => {
    const points = { vata: 0, pitta: 0, kapha: 0 };
    for (let i = 0; i < 18; i++) {
      const a = answers[i];
      if (a === null || a === 3) continue;
      points[DOSHA[a]] += 1;
    }
    const total = answers[9] === 3 ? 17 : 18;
    // largest-remainder rounding so the three whole numbers always total 100
    const parts = ["vata", "pitta", "kapha"].map((k) => ({ k, exact: (points[k] / total) * 100 }));
    const floors = parts.map((p) => ({ ...p, val: Math.floor(p.exact) }));
    let rem = 100 - floors.reduce((s, p) => s + p.val, 0);
    [...floors].sort((a, b) => (b.exact - b.val) - (a.exact - a.val)).forEach((p) => {
      if (rem > 0) { p.val += 1; rem -= 1; }
    });
    const pct = Object.fromEntries(floors.map((p) => [p.k, p.val]));

    const sorted = ["vata", "pitta", "kapha"].map((k) => ({ k, p: pct[k] })).sort((a, b) => b.p - a.p);
    const spread = sorted[0].p - sorted[2].p;
    let type, classification, doshas;
    if (spread <= 11) {
      type = "mixed";
      classification = "Mixed Pattern — No single pattern strongly predominates";
      doshas = [sorted[0].k, sorted[1].k];
    } else if (sorted[0].p - sorted[1].p >= 15) {
      type = "single";
      classification = `${DOSHA_LABEL[sorted[0].k]}-Dominant Pattern`;
      doshas = [sorted[0].k];
    } else {
      type = "dual";
      classification = `${DOSHA_LABEL[sorted[0].k]}–${DOSHA_LABEL[sorted[1].k]} Pattern`;
      doshas = [sorted[0].k, sorted[1].k];
    }

    const q19dosha = answers[18] !== null && answers[18] < 3 ? DOSHA[answers[18]] : null;
    const consistency = q19dosha && doshas.includes(q19dosha)
      ? "Pattern aligned"
      : "Pattern discrepancy — explore during detailed assessment";

    const domains = [];
    for (let i = 0; i < 18; i++) {
      const a = answers[i];
      if (a === null || a === 3) continue;
      if (doshas.includes(DOSHA[a]) && !domains.includes(QUESTIONS[i].domain)) domains.push(QUESTIONS[i].domain);
    }
    if (domains.length === 0) {
      for (let i = 0; i < 18; i++) if (answers[i] !== null && !domains.includes(QUESTIONS[i].domain)) domains.push(QUESTIONS[i].domain);
    }

    const explanation =
      type === "single" ? EXPLAIN.single(sorted[0].k) : type === "dual" ? EXPLAIN.dual(sorted[0].k, sorted[1].k) : EXPLAIN.mixed();

    return { pct, classification, consistency, q19dosha, domains, explanation };
  }, [answers]);

  const select = (val) => {
    setAnswers((a) => {
      const next = [...a];
      next[qi] = val;
      return next;
    });
    trackEvent("AssessmentAnswer", { question: qi + 1 });
  };

  const next = () => {
    if (answers[qi] === null || submitting) return;
    if (qi === 19) {
      if (linkedLead) completeLinkedLead();
      else setStep("lead");
    } else setQi(qi + 1);
  };

  const back = () => {
    if (qi === 0) setStep("intro");
    else setQi(qi - 1);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.concern) {
      toast.error("Please select your biggest concern.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment-leads`, {
        ...lead,
        vata_pct: analysis.pct.vata,
        pitta_pct: analysis.pct.pitta,
        kapha_pct: analysis.pct.kapha,
        classification: analysis.classification,
        q19_check: analysis.q19dosha ? DOSHA_LABEL[analysis.q19dosha] : null,
        q20_concern: answers[19] !== null ? QUESTIONS[19].options[answers[19]] : null,
        consistency: analysis.consistency,
      });
      trackEvent("AssessmentLead", { concern: lead.concern });
      trackLead({ content_name: "Brain Pattern Assessment", concern: lead.concern });
      setSubmitted(true);
      setStep("result");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const completeLinkedLead = async () => {
    setSubmitting(true);
    try {
      await axios.patch(`${API}/assessment-leads/${linkedLead.id}/complete`, {
        vata_pct: analysis.pct.vata,
        pitta_pct: analysis.pct.pitta,
        kapha_pct: analysis.pct.kapha,
        classification: analysis.classification,
        q19_check: analysis.q19dosha ? DOSHA_LABEL[analysis.q19dosha] : null,
        q20_concern: answers[19] !== null ? QUESTIONS[19].options[answers[19]] : null,
        consistency: analysis.consistency,
      });
      trackEvent("AssessmentLead", { concern: linkedLead.concern, source: "hero_form" });
      trackLead({ content_name: "Brain Pattern Assessment", concern: linkedLead.concern });
      setSubmitted(true);
      setStep("result");
    } catch (err) {
      toast.error("Could not save your result. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const explorePathways = () => {
    onClose();
    setTimeout(() => scrollToId("packages", "result_explore_pathways"), 150);
  };

  const progress = Math.round(((qi + 1) / 20) * 100);
  const q = QUESTIONS[qi];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="assessment-modal"
          className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-[#132C20]/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-[#FAF7F2] sm:rounded-3xl border border-[#E5D3AD] shadow-[0_32px_80px_rgb(19,44,32,0.45)]"
          >
            <button
              data-testid="assessment-close-btn"
              onClick={onClose}
              aria-label="Close assessment"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#F4EFE6] border border-[#E5D3AD] flex items-center justify-center text-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-[#FAF7F2] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 sm:p-10">
              {step === "intro" && (
                <div data-testid="assessment-intro">
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#B85D43]">
                    20-Question Self-Assessment
                  </p>
                  <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-[#1B3B2B] tracking-tight">
                    How often does this describe your child?
                  </h3>
                  <p className="mt-4 text-sm text-[#5C6E62] leading-relaxed">
                    Answer based on how your child generally behaves across everyday situations, rather than one
                    particularly good or difficult day.
                  </p>
                  <div className="mt-6 bg-[#F4EFE6] border border-[#E5D3AD]/60 rounded-2xl p-5">
                    <p className="text-sm text-[#2D3A32] leading-relaxed">
                      For each question, choose the option that best describes your child. There are no right or wrong
                      answers.
                    </p>
                  </div>
                  <p className="mt-5 text-[11px] text-[#8a968e] leading-relaxed">
                    A preliminary self-evaluation for educational purposes only — not a medical diagnosis or definitive
                    assessment.
                  </p>
                  <button
                    data-testid="assessment-start-btn"
                    onClick={() => setStep("q")}
                    className="mt-6 w-full bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-sm px-8 py-4 rounded-full hover:bg-[#132C20] transition-colors"
                  >
                    BEGIN ASSESSMENT
                  </button>
                </div>
              )}

              {step === "q" && (
                <div data-testid="assessment-question">
                  <div className="flex items-center justify-between text-[11px] font-semibold tracking-[0.18em] uppercase text-[#5C6E62]">
                    <span data-testid="assessment-progress-label">Question {qi + 1} of 20</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="mt-2.5 h-1.5 rounded-full bg-[#E5D3AD]/60 overflow-hidden">
                    <div
                      data-testid="assessment-progress-bar"
                      className="h-full bg-[#C5A059] rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={qi}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="mt-8 font-serif text-xl sm:text-2xl text-[#1B3B2B] leading-relaxed">
                        {q.q}
                      </p>
                      <div className="mt-6 space-y-2.5">
                        {q.options.map((opt, val) => (
                          <button
                            key={val}
                            data-testid={`assessment-answer-${val}`}
                            onClick={() => select(val)}
                            className={`w-full flex items-start gap-3.5 rounded-xl border px-4 sm:px-5 py-4 text-left transition-all duration-300 ${
                              answers[qi] === val
                                ? "bg-[#1B3B2B] text-[#FAF7F2] border-[#1B3B2B]"
                                : "bg-white text-[#2D3A32] border-[#E2DACD] hover:border-[#C5A059]"
                            }`}
                          >
                            <span
                              className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                                answers[qi] === val ? "bg-[#C5A059] text-[#1B3B2B]" : "bg-[#F4EFE6] text-[#1B3B2B] border border-[#E2DACD]"
                              }`}
                            >
                              {String.fromCharCode(65 + val)}
                            </span>
                            <span className="text-sm leading-relaxed">{opt}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                  <div className="mt-8 flex gap-3">
                    <button
                      data-testid="assessment-back-btn"
                      onClick={back}
                      className="flex items-center gap-2 border border-[#1B3B2B]/25 text-[#1B3B2B] text-sm font-semibold px-6 py-3.5 rounded-full hover:bg-[#F4EFE6] transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> BACK
                    </button>
                    <button
                      data-testid="assessment-next-btn"
                      onClick={next}
                      disabled={answers[qi] === null}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#1B3B2B] text-[#FAF7F2] text-sm font-semibold tracking-wide px-6 py-3.5 rounded-full hover:bg-[#132C20] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {qi === 19 ? "CONTINUE" : "NEXT"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === "lead" && (
                <form onSubmit={submitLead} data-testid="assessment-lead-form">
                  <h3 className="font-serif text-2xl sm:text-3xl text-[#1B3B2B] tracking-tight">
                    Your Child's Brain Pattern Preview is Ready
                  </h3>
                  <p className="mt-3 text-sm text-[#5C6E62] leading-relaxed">
                    Enter your details below to see your child's preliminary pattern.
                  </p>
                  <div className="mt-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="assessment-parent-name" required className={inputCls} placeholder="Parent Name *" value={lead.parent_name} onChange={(e) => setLead({ ...lead, parent_name: e.target.value })} />
                      <input data-testid="assessment-child-age" required className={inputCls} placeholder="Child Age *" value={lead.child_age} onChange={(e) => setLead({ ...lead, child_age: e.target.value })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="assessment-whatsapp" required className={inputCls} placeholder="WhatsApp Number *" value={lead.whatsapp} onChange={(e) => setLead({ ...lead, whatsapp: e.target.value })} />
                      <input data-testid="assessment-email" required type="email" className={inputCls} placeholder="Email *" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                    </div>
                    <input data-testid="assessment-city-state" required className={inputCls} placeholder="City / State *" value={lead.city_state} onChange={(e) => setLead({ ...lead, city_state: e.target.value })} />
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-[#1B3B2B] mb-2.5">
                        What is your biggest concern about your child right now? <span className="text-[#B85D43]">*</span>
                      </p>
                      <div data-testid="assessment-concern-group" className="flex flex-wrap gap-2">
                        {CONCERNS.map((c) => (
                          <button
                            type="button"
                            key={c}
                            data-testid={`assessment-concern-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            onClick={() => setLead({ ...lead, concern: c })}
                            className={`text-xs font-medium px-4 py-2.5 rounded-full border transition-all duration-300 ${
                              lead.concern === c
                                ? "bg-[#1B3B2B] text-[#FAF7F2] border-[#1B3B2B]"
                                : "bg-white text-[#2D3A32] border-[#E2DACD] hover:border-[#C5A059]"
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid="assessment-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="mt-7 w-full flex items-center justify-center gap-2 bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-sm px-8 py-4 rounded-full hover:bg-[#132C20] transition-colors disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    SHOW MY CHILD'S BRAIN PATTERN
                  </button>
                  <p className="mt-4 text-[11px] text-[#8a968e] text-center leading-relaxed">
                    Your information is used only to share your preliminary result and contact you regarding your
                    request.
                  </p>
                </form>
              )}

              {step === "result" && submitted && (
                <div data-testid="assessment-result">
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#B85D43] pr-10">
                    A preliminary self-assessment based on the responses provided
                  </p>
                  <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-[#1B3B2B] tracking-tight">
                    Your Child's Preliminary Ayurvedic Pattern
                  </h3>
                  <p data-testid="result-classification" className="mt-4 font-serif italic text-xl sm:text-2xl text-[#B85D43]">
                    {analysis.classification}
                  </p>

                  <div className="mt-6 space-y-3">
                    {[
                      ["vata", "Vata-aligned responses"],
                      ["pitta", "Pitta-aligned responses"],
                      ["kapha", "Kapha-aligned responses"],
                    ].map(([k, label]) => (
                      <div key={k} data-testid={`result-${k}`} className="bg-white border border-[#E2DACD] rounded-2xl px-5 py-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold tracking-[0.14em] text-[#1B3B2B]">{label}</span>
                          <span className="font-serif text-xl text-[#1B3B2B]">{analysis.pct[k]}%</span>
                        </div>
                        <div className="mt-2.5 h-1.5 rounded-full bg-[#E5D3AD]/60 overflow-hidden">
                          <div className="h-full bg-[#C5A059] rounded-full" style={{ width: `${analysis.pct[k]}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-5 text-sm text-[#5C6E62] leading-relaxed">{analysis.explanation}</p>

                  <div className="mt-7" data-testid="functional-snapshot">
                    <h4 className="font-serif text-xl text-[#1B3B2B]">Areas Highlighted by Your Responses</h4>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {analysis.domains.map((a) => (
                        <span key={a} className="bg-[#F4EFE6] text-[#1B3B2B] px-4 py-2 rounded-full text-xs font-semibold tracking-wide border border-[#E5D3AD]">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>

                  <p className="mt-6 text-[11px] text-[#8a968e] leading-relaxed">
                    This self-assessment identifies patterns within our Ayurvedic Brain Type framework based on
                    parent-reported responses. It is intended for educational purposes and does not diagnose autism,
                    ADHD, speech delay or any other developmental or neurological condition.
                  </p>

                  <div className="mt-7 bg-[#EAF0EC] border border-[#5B7566]/20 rounded-2xl p-6">
                    <h4 className="font-serif text-xl text-[#1B3B2B]">Want to Understand Your Child More Deeply?</h4>
                    <p className="mt-2.5 text-sm text-[#5C6E62] leading-relaxed">
                      This preliminary self-assessment is only one layer. A detailed consultation explores the child's
                      broader functional patterns and helps guide the next steps.
                    </p>
                    <button
                      data-testid="result-explore-pathways-btn"
                      onClick={explorePathways}
                      className="mt-5 w-full bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-sm px-8 py-4 rounded-full hover:bg-[#132C20] transition-colors"
                    >
                      EXPLORE CONSULTATION PATHWAYS
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
